import React from 'react';
import { 
  Settings, 
  Database, 
  MessageSquare, 
  ChevronLeft, 
  ChevronRight,
  Home,
  History,
  Star,
  HelpCircle
} from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import clsx from 'clsx';

const navItems = [
  {
    id: 'config',
    label: '配置识别',
    icon: Settings,
    description: '自动识别 API 配置',
  },
  {
    id: 'providers',
    label: '供应商管理',
    icon: Database,
    description: '管理 API 供应商',
  },
  {
    id: 'chat',
    label: '对话测试',
    icon: MessageSquare,
    description: '测试 API 对话',
  },
  {
    id: 'history',
    label: '历史记录',
    icon: History,
    description: '查看历史对话',
  },
  {
    id: 'templates',
    label: '对话模板',
    icon: Star,
    description: '管理对话模板',
  },
  {
    id: 'help',
    label: '帮助文档',
    icon: HelpCircle,
    description: '查看使用帮助',
  },
];

export const Sidebar: React.FC = () => {
  const { 
    ui: { sidebarCollapsed, activeTab }, 
    toggleSidebar, 
    setActiveTab 
  } = useAppStore();

  return (
    <aside className={clsx(
      'flex flex-col h-full bg-white border-r border-gray-200 transition-all duration-300',
      sidebarCollapsed ? 'w-20' : 'w-64'
    )}>
      {/* Logo 区域 */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className={clsx(
          'flex items-center gap-3 transition-all duration-300',
          sidebarCollapsed ? 'justify-center w-full' : ''
        )}>
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
            <Home className="w-5 h-5 text-white" />
          </div>
          {!sidebarCollapsed && (
            <div>
              <h1 className="text-lg font-bold text-gray-900">API 对话</h1>
              <p className="text-xs text-gray-500">管理系统</p>
            </div>
          )}
        </div>
        
        <button
          onClick={toggleSidebar}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          title={sidebarCollapsed ? '展开侧边栏' : '折叠侧边栏'}
        >
          {sidebarCollapsed ? (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronLeft className="w-4 h-4 text-gray-500" />
          )}
        </button>
      </div>

      {/* 导航菜单 */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          
          return (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id as any)}
              className={clsx(
                'nav-link w-full text-left group relative',
                isActive && 'active'
              )}
              title={sidebarCollapsed ? item.label : ''}
            >
              <Icon className={clsx(
                'w-5 h-5 transition-colors flex-shrink-0',
                isActive ? 'text-primary-600' : 'text-gray-500 group-hover:text-gray-700'
              )} />
              
              {!sidebarCollapsed && (
                <div className="flex-1 min-w-0">
                  <span className="block font-medium">{item.label}</span>
                  <span className="block text-xs text-gray-500 truncate">
                    {item.description}
                  </span>
                </div>
              )}

              {/* 工具提示（折叠时显示） */}
              {sidebarCollapsed && (
                <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
                  <div className="font-medium">{item.label}</div>
                  <div className="text-xs text-gray-300 mt-0.5">{item.description}</div>
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-2 h-2 bg-gray-900 rotate-45"></div>
                </div>
              )}
            </button>
          );
        })}
      </nav>

      {/* 底部区域 */}
      <div className={clsx(
        'p-4 border-t border-gray-200',
        sidebarCollapsed ? 'text-center' : ''
      )}>
        {!sidebarCollapsed && (
          <div className="mb-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              系统状态
            </h3>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">供应商</span>
                <span className="font-medium text-primary-600">3</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">对话</span>
                <span className="font-medium text-accent-600">12</span>
              </div>
            </div>
          </div>
        )}
        
        <div className={clsx(
          'flex items-center gap-3 p-2 rounded-lg bg-gray-50',
          sidebarCollapsed && 'justify-center'
        )}>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
            <span className="text-white font-medium text-sm">U</span>
          </div>
          {!sidebarCollapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">用户</p>
              <p className="text-xs text-gray-500 truncate">个人使用</p>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};